import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProgress } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { Card, Overline } from "@/components/kit";
import { ProgressRing } from "@/components/ProgressRing";
import { Compass, Repeat, BookOpen, Grid, Lock, LogOut, ChevronRight } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";
import { apiError } from "@/lib/api";

const MENU = [
  { label: "View my journey", to: "/journey", icon: Compass, testid: "menu-journey" },
  { label: "Then & Now", to: "/shift", icon: Repeat, testid: "menu-shift" },
  { label: "Edit keepsake cover", to: "/cover", icon: Grid, testid: "menu-cover" },
  { label: "Reference maps", to: "/reference", icon: Grid, testid: "menu-reference" },
  { label: "Change password", to: "/change-password", icon: Lock, testid: "menu-change-password" },
  { label: "Read the framework", to: "/learn", icon: BookOpen, testid: "menu-learn" },
];

export default function You() {
  const navigate = useNavigate();
  const { user, logout, logoutAll, deleteAccount } = useAuth();
  const { data: progress } = useProgress();
  const [busy, setBusy] = useState(false);
  if (!progress) return <div className="text-inkfaint text-sm animate-pulse">Loading…</div>;

  const complete = progress.total_completed;
  const remaining = 12 - complete;
  const initial = (user?.name?.trim()?.[0] || "M").toUpperCase();

  const onDelete = async () => {
    setBusy(true);
    try {
      await deleteAccount();
      navigate("/welcome");
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto em-fade-up" data-testid="you-page">
      <Overline>Your Space</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-2 mb-8">You</h1>

      <Card className="flex items-center gap-6">
        <ProgressRing value={complete} total={12} center={
          <>
            <span className="font-serif text-3xl text-ink leading-none">{initial}</span>
            <span className="text-xs text-faint mt-1">{complete} / 12</span>
          </>
        } />
        <div>
          <div className="text-ink font-semibold">{user?.name}</div>
          <p className="text-inkfaint text-sm mt-1">
            {complete >= 12 ? "You have moved through every state." : `${remaining} states remain on your journey`}
          </p>
          <div className="flex gap-6 mt-4">
            <div><div className="font-serif text-2xl text-gold">{complete}</div><div className="text-xs text-faint">complete</div></div>
            <div><div className="font-serif text-2xl text-gold">{progress.months_visited}</div><div className="text-xs text-faint">{progress.months_visited === 1 ? "month active" : "months active"}</div></div>
          </div>
        </div>
      </Card>

      <div className="mt-6 space-y-2">
        {MENU.map(({ label, to, icon: Icon, testid }) => (
          <button key={to} onClick={() => navigate(to)} data-testid={testid}
            className="w-full flex items-center gap-4 bg-panel border border-line rounded-xl px-4 py-3.5 hover:border-goldmuted/40 transition-colors text-left">
            <Icon className="w-5 h-5 text-gold" />
            <span className="flex-1 text-ink">{label}</span>
            <ChevronRight className="w-5 h-5 text-inkfaint" />
          </button>
        ))}
      </div>

      <Card className="mt-6">
        <Overline>About</Overline>
        <p className="text-inksoft text-sm leading-relaxed mt-2">
          The Emergence Journal by Sarah Merron. A twelve-month experiment in directing your own
          emergence — ME₀ → ME₁ → ME₂ → … There is no final version. Only the person who is here now,
          and the next movement available.
        </p>
      </Card>

      <div className="mt-8 space-y-3">
        <button onClick={logout} data-testid="signout-btn"
          className="w-full inline-flex items-center justify-center gap-2 h-[54px] rounded-full border border-linestrong text-ink hover:bg-panel transition-colors">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
        <button onClick={logoutAll} data-testid="signout-all-btn"
          className="w-full h-[54px] rounded-full text-inkfaint hover:text-ink transition-colors text-sm">
          Log out of all devices
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="w-full text-emerror/90 hover:text-emerror text-sm py-2" data-testid="delete-account-link">
              Delete my account
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-panel border-line">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-2xl text-ink">Delete your account?</AlertDialogTitle>
              <AlertDialogDescription className="text-inkfaint">
                Deleting your account will immediately end your access and sign you out of all devices.
                Your account and journal data will then be permanently deleted after 30 days.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} disabled={busy} data-testid="delete-confirm"
                className="bg-emerror text-ink hover:opacity-90">Delete account</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
